/**
 * Personel login saat/gün kısıtlaması (Firestore: loginTimeRestriction).
 * Gün: 1–7 (Pazartesi–Pazar). Eski kayıtta alan yoksa kontrol yapılmaz (enabled ≠ true).
 */

const DEFAULT_LOGIN_TIME_RESTRICTION = {
  enabled: false,
  startTime: '09:00',
  endTime: '19:30',
  allowedDays: [1, 2, 3, 4, 5, 6]
};

export function timeToMinutes(time) {
  if (!time || typeof time !== 'string') return null;
  const parts = time.trim().split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Kısıtlama saatleri Türkiye saatiyle girilir. Sunucu (Cloud Functions) UTC'de çalıştığı için
 * getHours()/getDay() 3 saat geri kalıyordu (08:00 kısıtı pratikte 11:00'de açılıyordu).
 * Saat ve gün her zaman Europe/Istanbul'a göre hesaplanır.
 */
const ISTANBUL_CLOCK_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Istanbul',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});
const WEEKDAY_TO_APP_DAY = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

export function getIstanbulClock(now = new Date()) {
  const parts = {};
  ISTANBUL_CLOCK_FORMAT.formatToParts(now).forEach(({ type, value }) => {
    parts[type] = value;
  });
  return {
    appDay: WEEKDAY_TO_APP_DAY[parts.weekday],
    minutes: Number(parts.hour) * 60 + Number(parts.minute)
  };
}

export function isLoginAllowedByTimeRestriction(restriction, now = new Date()) {
  if (!restriction || restriction.enabled !== true) return true;

  const allowedDays = Array.isArray(restriction.allowedDays) ? restriction.allowedDays : [];

  const { appDay, minutes: current } = getIstanbulClock(now);

  if (!allowedDays.includes(appDay)) return false;

  const start = timeToMinutes(restriction.startTime || '09:00');
  const end = timeToMinutes(restriction.endTime || '19:30');

  if (start === null || end === null) return true;

  if (start === end) return true;

  if (start < end) {
    return current >= start && current <= end;
  }

  return current >= start || current <= end;
}

/** Firestore/UI için eksik/yozlaşmış değerleri varsayılanla tamamlar; undefined içermez. */
export function normalizeLoginTimeRestriction(input) {
  if (!input || typeof input !== 'object') {
    return { ...DEFAULT_LOGIN_TIME_RESTRICTION };
  }

  let allowedDays = Array.isArray(input.allowedDays) ? [...input.allowedDays] : [...DEFAULT_LOGIN_TIME_RESTRICTION.allowedDays];
  allowedDays = [...new Set(allowedDays.map((d) => Number(d)).filter((n) => n >= 1 && n <= 7))].sort((a, b) => a - b);
  if (!allowedDays.length) {
    allowedDays = [...DEFAULT_LOGIN_TIME_RESTRICTION.allowedDays];
  }

  const st = typeof input.startTime === 'string' && input.startTime.trim() ? input.startTime.trim() : DEFAULT_LOGIN_TIME_RESTRICTION.startTime;
  const en = typeof input.endTime === 'string' && input.endTime.trim() ? input.endTime.trim() : DEFAULT_LOGIN_TIME_RESTRICTION.endTime;

  return {
    enabled: input.enabled === true,
    startTime: timeToMinutes(st) === null ? DEFAULT_LOGIN_TIME_RESTRICTION.startTime : st,
    endTime: timeToMinutes(en) === null ? DEFAULT_LOGIN_TIME_RESTRICTION.endTime : en,
    allowedDays
  };
}

export const LOGIN_TIME_RESTRICTION_DENIED_MESSAGE =
  'Giriş izniniz şu anda aktif değil. Lütfen yetkili kişiyle iletişime geçiniz.';
